import React, { useEffect, useMemo, useState } from 'react';
import {
  Card,
  Row,
  Col,
  Form,
  Button,
  Alert,
  Table,
  Spinner,
  Badge,
  InputGroup,
} from 'react-bootstrap';
import AdminLayout from '../components/AdminLayout';
import PaginationControls from '../components/common/PaginationControls';
import api from '../lib/api';
import { useToast } from '../components/common/ToastProvider';

const defaultWhatsApp = {
  verify_token: '',
  phone_number_id: '',
  api_token: '',
  webhook_enabled: false,
  menu_title: 'Welcome',
  menu_body: 'Hello, how can we help?',
  menu_button_text: 'Menus',
  menu_options: [],
  quick_reply_intro_text: 'How can I help you next?',
  quick_reply_intro_options: [],
  quick_reply_stuck_text:
    "It looks like you're still facing issues. Would you like to talk to a real person?",
  quick_reply_stuck_options: [],
  action_reply_map: {},
  support_email: '',
  support_phone: '',
  support_address: '',
  allowed_media_types: 'text,image,document',
  max_messages_per_hour: 60,
};

const defaultGateway = {
  stripe_enabled: false,
  stripe_public_key: '',
  stripe_secret_key: '',
  stripe_webhook_secret: '',
  paystack_enabled: false,
  paystack_public_key: '',
  paystack_secret_key: '',
  paystack_callback_url: '',
};

const defaultSite = {
  site_name: '',
  logo_url: '',
  hero_title: '',
  hero_subtitle: '',
  hero_cta_text: '',
  hero_cta_url: '',
  footer_text: '',
  seo_title: '',
  seo_description: '',
  seo_keywords: '',
  smtp_host: '',
  smtp_port: 587,
  smtp_username: '',
  smtp_password: '',
  smtp_use_tls: true,
  google_client_id: '',
  google_client_secret: '',
  google_enabled: false,
  cloudinary_enabled: false,
  cloudinary_cloud_name: '',
  cloudinary_api_key: '',
  cloudinary_api_secret: '',
  cloudinary_api_secret_masked: '',
  cloudinary_folder: 'site-assets',
  admin_notification_email: 'saasglobalhub@gmail.com',
};

const FEATURE_OPTIONS = [
  { code: 'feature_widget_enabled', label: 'Widget Chat' },
  { code: 'feature_training_data_enabled', label: 'Training Data' },
  { code: 'feature_live_agent_handover', label: 'Live Agent Handover' },
  { code: 'feature_whatsapp_enabled', label: 'WhatsApp Channel' },
  { code: 'feature_brand_router_advanced', label: 'Advanced Brand Router' },
  { code: 'feature_custom_provider_keys', label: 'Custom Model Keys' },
];

const LIMIT_OPTIONS = [
  { code: 'max_widgets', label: 'Max Widgets' },
  { code: 'max_admin_seats', label: 'Max Admin Seats' },
  { code: 'max_monthly_messages', label: 'Max Monthly Messages' },
  { code: 'max_training_sources', label: 'Max Training Sources' },
  { code: 'max_url_training_jobs_per_day', label: 'Max URL Jobs / Day' },
  { code: 'max_custom_model_keys', label: 'Max Custom Model Keys' },
  { code: 'max_support_agents', label: 'Max Support Agents' },
];

const emptyCmsPage = {
  id: null,
  slug: '',
  title: '',
  content: '',
  seo_title: '',
  seo_description: '',
  show_in_menu: false,
  show_in_footer: true,
  is_published: true,
};

const emptyMenuOption = { id: '', title: '', description: '' };
const emptyReplyOption = { id: '', title: '' };
const emptyActionMap = { id: '', text: '' };

function SectionCard({ title, right, children }) {
  return (
    <Card className="admin-surface-card mb-4">
      <Card.Body className="p-4">
        <div className="d-flex justify-content-between align-items-center flex-wrap gap-2 mb-3">
          <div className="admin-section-title mb-0">{title}</div>
          {right || null}
        </div>
        {children}
      </Card.Body>
    </Card>
  );
}

function SoftBlock({ title, children, className = '' }) {
  return (
    <Card
      className={`border-0 rounded-4 h-100 ${className}`}
      style={{ background: '#f8fafc' }}
    >
      <Card.Body className="p-3">
        <div className="fw-semibold mb-3" style={{ color: '#0f172a' }}>
          {title}
        </div>
        {children}
      </Card.Body>
    </Card>
  );
}

function SensitiveField({ label, value, onChange, placeholder }) {
  const [visible, setVisible] = useState(false);

  return (
    <Form.Group>
      <Form.Label>{label}</Form.Label>
      <InputGroup>
        <Form.Control
          type={visible ? 'text' : 'password'}
          value={value || ''}
          onChange={onChange}
          placeholder={placeholder}
          autoComplete="off"
        />
        <Button
          variant="outline-secondary"
          type="button"
          onClick={() => setVisible((prev) => !prev)}
        >
          {visible ? 'Hide' : 'Show'}
        </Button>
      </InputGroup>
    </Form.Group>
  );
}

export default function AdminSettingsPage() {
  const [msg, setMsg] = useState('');
  const [msgType, setMsgType] = useState('success');

  const [workspace, setWorkspace] = useState(null);
  const [wa, setWa] = useState(defaultWhatsApp);
  const [waActionRows, setWaActionRows] = useState([]);
  const [agents, setAgents] = useState([]);
  const [gateway, setGateway] = useState(defaultGateway);
  const [siteSettings, setSiteSettings] = useState(defaultSite);
  const [cmsPage, setCmsPage] = useState(emptyCmsPage);
  const [cmsList, setCmsList] = useState({
    results: [],
    previous: null,
    next: null,
    page: 1,
  });
  const [templates, setTemplates] = useState([]);
  const [entitlements, setEntitlements] = useState([]);
  const [subscription, setSubscription] = useState(null);
  const [plans, setPlans] = useState([]);
  const [summary, setSummary] = useState(null);
  const [history, setHistory] = useState({
    results: [],
    previous: null,
    next: null,
    page: 1,
  });
  const [planCatalog, setPlanCatalog] = useState([]);
  const [loading, setLoading] = useState(true);
  const { showToast } = useToast();

  const showMessage = (text, type = 'success') => {
    setMsg(text);
    setMsgType(type);
    showToast(text, type === 'danger' ? 'danger' : type === 'warning' ? 'warning' : 'success');
  };

  const parsePlanMeta = (meta) => {
    if (!meta) return {};
    if (typeof meta === 'string') {
      try {
        return JSON.parse(meta);
      } catch {
        return {};
      }
    }
    return meta;
  };

  const withPlanDefaults = (plan) => {
    const current = parsePlanMeta(plan?.metadata);
    const featureDefaults = FEATURE_OPTIONS.reduce(
      (acc, opt) => ({ ...acc, [opt.code]: false }),
      {}
    );
    const limitDefaults = LIMIT_OPTIONS.reduce(
      (acc, opt) => ({ ...acc, [opt.code]: null }),
      {}
    );

    return {
      ...plan,
      metadata: {
        features: { ...featureDefaults, ...(current.features || {}) },
        limits: { ...limitDefaults, ...(current.limits || {}) },
      },
    };
  };

  const historyPageOrDefault = () => history.page || 1;
  const cmsPageOrDefault = () => cmsList.page || 1;

  const load = async (historyPage = 1, cmsPageNum = 1) => {
    try {
      setLoading(true);
      await api.post('/admin/settings/bootstrap/');

      const [ws, waRes, ent, sub, gw, sum, hist, site, cms, tmpl, planCatalogRes, agentsRes] =
        await Promise.all([
          api.get('/admin/settings/workspace/'),
          api.get('/admin/settings/whatsapp/'),
          api.get('/admin/settings/entitlements/'),
          api.get('/admin/settings/subscription/'),
          api.get('/admin/settings/payment-gateways/'),
          api.get('/admin/settings/summary/'),
          api.get('/admin/settings/payment-history/', {
            params: { page: historyPage, page_size: 10 },
          }),
          api.get('/admin/settings/site-content/'),
          api.get('/admin/settings/cms-pages/', {
            params: { page: cmsPageNum, page_size: 8 },
          }),
          api.get('/admin/settings/email-templates/'),
          api.get('/admin/settings/plans/'),
          api.get('/admin/settings/support-agents/'),
        ]);

      setWorkspace(ws.data);
      setWa({ ...defaultWhatsApp, ...(waRes.data || {}) });

      const actionMap = waRes.data?.action_reply_map || {};
      setWaActionRows(
        Object.entries(actionMap).map(([id, text]) => ({
          id,
          text: String(text || ''),
        }))
      );

      setEntitlements(ent.data.entitlements || []);
      setSubscription(sub.data.subscription || null);
      setPlans(sub.data.plans || []);
      setGateway({ ...defaultGateway, ...(gw.data || {}) });
      setSummary(sum.data || null);
      setHistory({ ...(hist.data || {}), page: historyPage });
      setSiteSettings({ ...defaultSite, ...(site.data || {}) });
      setCmsList({ ...(cms.data || {}), page: cmsPageNum });
      setTemplates(tmpl.data || []);
      setPlanCatalog((planCatalogRes.data || []).map(withPlanDefaults));
      setAgents(Array.isArray(agentsRes.data) ? agentsRes.data : []);
    } catch {
      showMessage('Failed to load settings', 'danger');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const updateWaOptionRow = (field, idx, patch) => {
    const rows = [...(wa[field] || [])];
    rows[idx] = { ...(rows[idx] || {}), ...patch };
    setWa({ ...wa, [field]: rows });
  };

  const addWaOptionRow = (field, template = {}) => {
    setWa({ ...wa, [field]: [...(wa[field] || []), template] });
  };

  const removeWaOptionRow = (field, idx) => {
    setWa({ ...wa, [field]: (wa[field] || []).filter((_, i) => i !== idx) });
  };

  const togglePlanFeature = (index, featureCode, enabled) => {
    const copy = [...planCatalog];
    const current = parsePlanMeta(copy[index]?.metadata);

    copy[index] = {
      ...copy[index],
      metadata: {
        ...current,
        features: { ...(current.features || {}), [featureCode]: !!enabled },
      },
    };

    setPlanCatalog(copy);
  };

  const setPlanLimit = (index, limitCode, value) => {
    const copy = [...planCatalog];
    const current = parsePlanMeta(copy[index]?.metadata);

    copy[index] = {
      ...copy[index],
      metadata: {
        ...current,
        limits: {
          ...(current.limits || {}),
          [limitCode]: value === '' ? null : Number(value),
        },
      },
    };

    setPlanCatalog(copy);
  };

  const saveWorkspace = async () => {
    await api.post('/admin/settings/workspace/', workspace);
    showMessage('Workspace saved successfully.');
  };

  const saveWhatsApp = async () => {
    const nextActionMap = {};
    waActionRows.forEach((row) => {
      const key = (row.id || '').trim();
      if (key) nextActionMap[key] = row.text || '';
    });

    await api.post('/admin/settings/whatsapp/', {
      ...wa,
      action_reply_map: nextActionMap,
    });

    showMessage('WhatsApp settings saved successfully.');
  };

  const addSupportAgent = () => {
    setAgents((prev) => [
      ...prev,
      {
        name: '',
        email: '',
        password: '',
        is_online: true,
        is_active: true,
        max_active_chats: 10,
      },
    ]);
  };

  const saveSupportAgents = async () => {
    const cleaned = (agents || []).map((agent) => ({
      ...agent,
      email: (agent.email || '').trim().toLowerCase(),
    }));
    await api.post('/admin/settings/support-agents/', { agents: cleaned });
    showMessage('Support agents saved successfully.');
    load(historyPageOrDefault(), cmsPageOrDefault());
  };

  const removeSupportAgent = async (idx) => {
    const current = agents[idx];
    if (current?.id) {
      await api.delete(`/admin/settings/support-agents/${current.id}/`);
    }
    setAgents((prev) => prev.filter((_, i) => i !== idx));
    showMessage('Support agent removed successfully.');
  };

  const saveEntitlements = async () => {
    await api.post('/admin/settings/entitlements/', { entitlements });
    showMessage('Entitlements saved successfully.');
  };

  const saveGateways = async () => {
    await api.post('/admin/settings/payment-gateways/', gateway);
    showMessage('Payment gateway settings saved successfully.');
    load(history.page || 1, cmsList.page || 1);
  };

  const saveSiteSettings = async () => {
    await api.post('/admin/settings/site-content/', siteSettings);
    showMessage('Site settings saved successfully.');
    load(history.page || 1, cmsList.page || 1);
  };

  const saveTemplates = async () => {
    await api.post('/admin/settings/email-templates/', { templates });
    showMessage('Email templates saved successfully.');
  };

  const savePlanCatalog = async () => {
    await api.post('/admin/settings/plans/', {
      plans: planCatalog.map(withPlanDefaults),
    });
    showMessage('Plan catalog saved successfully.');
    load(historyPageOrDefault(), cmsPageOrDefault());
  };

  const reapplyEntitlements = async () => {
    await api.post('/admin/settings/subscription/reapply-entitlements/', {});
    showMessage('Entitlements reapplied successfully.');
    load(historyPageOrDefault(), cmsPageOrDefault());
  };

  const updateSubscription = async (
    planId,
    provider = 'stripe',
    billing_cycle = 'monthly'
  ) => {
    await api.post('/admin/settings/subscription/', {
      plan_id: planId,
      provider,
      billing_cycle,
    });
    showMessage('Subscription updated successfully.');
    load(history.page || 1, cmsList.page || 1);
  };

  const startAdminCheckout = async (
    planId,
    provider = 'stripe',
    billing_cycle = 'monthly'
  ) => {
    const origin = window.location.origin;

    const { data } = await api.post('/admin/settings/checkout/', {
      plan_id: planId,
      provider,
      billing_cycle,
      success_url: `${origin}/admin/settings?checkout=success`,
      cancel_url: `${origin}/admin/settings?checkout=cancel`,
    });

    const url = data?.checkout?.url || data?.checkout?.authorization_url;

    if (!url) {
      showMessage('Checkout initialized but no redirect URL returned.', 'danger');
      return;
    }

    window.location.href = url;
  };

  const saveCmsPage = async () => {
    await api.post('/admin/settings/cms-pages/', cmsPage);
    showMessage('CMS page saved successfully.');
    setCmsPage(emptyCmsPage);
    load(history.page || 1, cmsList.page || 1);
  };

  const deleteCmsPage = async (id) => {
    if (!window.confirm('Delete this CMS page?')) return;
    await api.delete(`/admin/settings/cms-pages/${id}/`);
    showMessage('CMS page deleted successfully.');
    load(history.page || 1, cmsList.page || 1);
  };

  const summaryCards = useMemo(() => {
    if (!summary) return [];
    return [
      {
        title: 'Workspace Messages',
        value: summary.workspace_summary?.chat_messages ?? 0,
      },
      {
        title: 'Workspace Widgets',
        value: summary.workspace_summary?.widgets ?? 0,
      },
      {
        title: 'Workspace Payment Events',
        value: summary.workspace_summary?.events ?? 0,
      },
    ];
  }, [summary]);

  if (loading) {
    return (
      <AdminLayout
        title="Platform Settings"
        subtitle="Manage platform-wide configuration, billing, CMS, email templates, and integrations."
      >
        <Card className="admin-surface-card">
          <Card.Body className="text-center py-5">
            <Spinner animation="border" />
          </Card.Body>
        </Card>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout
      title="Platform Settings"
      subtitle="Manage platform-wide configuration, billing, CMS, email templates, integrations, and subscription features."
    >
      {msg ? <Alert variant={msgType}>{msg}</Alert> : null}

      {summaryCards.length ? (
        <Row className="g-3 mb-4">
          {summaryCards.map((item) => (
            <Col md={4} key={item.title}>
              <div className="admin-stat-card">
                <div className="text-muted small fw-semibold mb-2">{item.title}</div>
                <h3 className="mb-0 fw-bold">{item.value}</h3>
              </div>
            </Col>
          ))}
        </Row>
      ) : null}

      {workspace ? (
        <SectionCard title="Workspace">
          <Row className="g-3">
            <Col md={4}>
              <Form.Group>
                <Form.Label>Workspace Name</Form.Label>
                <Form.Control
                  value={workspace.name || ''}
                  onChange={(e) => setWorkspace({ ...workspace, name: e.target.value })}
                  placeholder="Workspace name"
                />
              </Form.Group>
            </Col>

            <Col md={4}>
              <Form.Group>
                <Form.Label>Workspace Slug</Form.Label>
                <Form.Control
                  value={workspace.slug || ''}
                  onChange={(e) => setWorkspace({ ...workspace, slug: e.target.value })}
                  placeholder="Workspace slug"
                />
              </Form.Group>
            </Col>

            <Col md={3}>
              <Form.Group>
                <Form.Label>Timezone</Form.Label>
                <Form.Control
                  value={workspace.timezone || ''}
                  onChange={(e) =>
                    setWorkspace({ ...workspace, timezone: e.target.value })
                  }
                  placeholder="Timezone"
                />
              </Form.Group>
            </Col>

            <Col md={1} className="d-flex align-items-end">
              <Button onClick={saveWorkspace} className="w-100 admin-action-btn">
                Save
              </Button>
            </Col>
          </Row>
        </SectionCard>
      ) : null}

      <SectionCard title="Website / SEO / SMTP / Google / Cloudinary Settings">
        <Row className="g-4">
          <Col xl={6}>
            <SoftBlock title="Branding & Hero Content">
              <Row className="g-3">
                <Col md={12}>
                  <Form.Group>
                    <Form.Label>Site Name</Form.Label>
                    <Form.Control
                      value={siteSettings.site_name || ''}
                      onChange={(e) =>
                        setSiteSettings({ ...siteSettings, site_name: e.target.value })
                      }
                      placeholder="Site name"
                    />
                  </Form.Group>
                </Col>

                <Col md={12}>
                  <Form.Group>
                    <Form.Label>Hero Title</Form.Label>
                    <Form.Control
                      value={siteSettings.hero_title || ''}
                      onChange={(e) =>
                        setSiteSettings({ ...siteSettings, hero_title: e.target.value })
                      }
                      placeholder="Hero title"
                    />
                  </Form.Group>
                </Col>

                <Col md={12}>
                  <Form.Group>
                    <Form.Label>Hero Subtitle</Form.Label>
                    <Form.Control
                      value={siteSettings.hero_subtitle || ''}
                      onChange={(e) =>
                        setSiteSettings({
                          ...siteSettings,
                          hero_subtitle: e.target.value,
                        })
                      }
                      placeholder="Hero subtitle"
                    />
                  </Form.Group>
                </Col>

                <Col md={6}>
                  <Form.Group>
                    <Form.Label>Hero CTA Text</Form.Label>
                    <Form.Control
                      value={siteSettings.hero_cta_text || ''}
                      onChange={(e) =>
                        setSiteSettings({
                          ...siteSettings,
                          hero_cta_text: e.target.value,
                        })
                      }
                      placeholder="Hero CTA text"
                    />
                  </Form.Group>
                </Col>

                <Col md={6}>
                  <Form.Group>
                    <Form.Label>Hero CTA URL</Form.Label>
                    <Form.Control
                      value={siteSettings.hero_cta_url || ''}
                      onChange={(e) =>
                        setSiteSettings({
                          ...siteSettings,
                          hero_cta_url: e.target.value,
                        })
                      }
                      placeholder="Hero CTA URL"
                    />
                  </Form.Group>
                </Col>

                <Col md={12}>
                  <Form.Group>
                    <Form.Label>Footer Text</Form.Label>
                    <Form.Control
                      value={siteSettings.footer_text || ''}
                      onChange={(e) =>
                        setSiteSettings({
                          ...siteSettings,
                          footer_text: e.target.value,
                        })
                      }
                      placeholder="Footer text"
                    />
                  </Form.Group>
                </Col>
              </Row>
            </SoftBlock>
          </Col>

          <Col xl={6}>
            <SoftBlock title="SEO Configuration">
              <Row className="g-3">
                <Col md={12}>
                  <Form.Group>
                    <Form.Label>SEO Title</Form.Label>
                    <Form.Control
                      value={siteSettings.seo_title || ''}
                      onChange={(e) =>
                        setSiteSettings({ ...siteSettings, seo_title: e.target.value })
                      }
                      placeholder="SEO title"
                    />
                  </Form.Group>
                </Col>

                <Col md={12}>
                  <Form.Group>
                    <Form.Label>SEO Description</Form.Label>
                    <Form.Control
                      as="textarea"
                      rows={3}
                      value={siteSettings.seo_description || ''}
                      onChange={(e) =>
                        setSiteSettings({
                          ...siteSettings,
                          seo_description: e.target.value,
                        })
                      }
                      placeholder="SEO description"
                    />
                  </Form.Group>
                </Col>

                <Col md={12}>
                  <Form.Group>
                    <Form.Label>SEO Keywords</Form.Label>
                    <Form.Control
                      value={siteSettings.seo_keywords || ''}
                      onChange={(e) =>
                        setSiteSettings({
                          ...siteSettings,
                          seo_keywords: e.target.value,
                        })
                      }
                      placeholder="SEO keywords"
                    />
                  </Form.Group>
                </Col>
              </Row>
            </SoftBlock>
          </Col>

          <Col xl={6}>
            <SoftBlock title="SMTP Settings">
              <Row className="g-3">
                <Col md={6}>
                  <Form.Group>
                    <Form.Label>SMTP Host</Form.Label>
                    <Form.Control
                      value={siteSettings.smtp_host || ''}
                      onChange={(e) =>
                        setSiteSettings({ ...siteSettings, smtp_host: e.target.value })
                      }
                      placeholder="SMTP host"
                    />
                  </Form.Group>
                </Col>

                <Col md={6}>
                  <Form.Group>
                    <Form.Label>SMTP Port</Form.Label>
                    <Form.Control
                      value={siteSettings.smtp_port || ''}
                      onChange={(e) =>
                        setSiteSettings({
                          ...siteSettings,
                          smtp_port: Number(e.target.value) || 0,
                        })
                      }
                      placeholder="SMTP port"
                    />
                  </Form.Group>
                </Col>

                <Col md={6}>
                  <Form.Group>
                    <Form.Label>SMTP Username</Form.Label>
                    <Form.Control
                      value={siteSettings.smtp_username || ''}
                      onChange={(e) =>
                        setSiteSettings({
                          ...siteSettings,
                          smtp_username: e.target.value,
                        })
                      }
                      placeholder="SMTP username"
                    />
                  </Form.Group>
                </Col>

                <Col md={6}>
                  <SensitiveField
                    label="SMTP Password"
                    value={siteSettings.smtp_password || ''}
                    onChange={(e) =>
                      setSiteSettings({
                        ...siteSettings,
                        smtp_password: e.target.value,
                      })
                    }
                    placeholder="SMTP password"
                  />
                </Col>

                <Col md={12}>
                  <Form.Check
                    type="switch"
                    label="SMTP TLS"
                    checked={!!siteSettings.smtp_use_tls}
                    onChange={(e) =>
                      setSiteSettings({
                        ...siteSettings,
                        smtp_use_tls: e.target.checked,
                      })
                    }
                  />
                </Col>
              </Row>
            </SoftBlock>
          </Col>

          <Col xl={6}>
            <SoftBlock title="Google Login & Notifications">
              <Row className="g-3">
                <Col md={12}>
                  <Form.Group>
                    <Form.Label>Google Client ID</Form.Label>
                    <Form.Control
                      value={siteSettings.google_client_id || ''}
                      onChange={(e) =>
                        setSiteSettings({
                          ...siteSettings,
                          google_client_id: e.target.value,
                        })
                      }
                      placeholder="Google client ID"
                    />
                  </Form.Group>
                </Col>

                <Col md={12}>
                  <SensitiveField
                    label="Google Client Secret"
                    value={siteSettings.google_client_secret || ''}
                    onChange={(e) =>
                      setSiteSettings({
                        ...siteSettings,
                        google_client_secret: e.target.value,
                      })
                    }
                    placeholder="Google client secret"
                  />
                </Col>

                <Col md={12}>
                  <Form.Check
                    type="switch"
                    label="Google login enabled"
                    checked={!!siteSettings.google_enabled}
                    onChange={(e) =>
                      setSiteSettings({
                        ...siteSettings,
                        google_enabled: e.target.checked,
                      })
                    }
                  />
                </Col>

                <Col md={12}>
                  <Form.Group>
                    <Form.Label>Admin Notification Email</Form.Label>
                    <Form.Control
                      value={siteSettings.admin_notification_email || ''}
                      onChange={(e) =>
                        setSiteSettings({
                          ...siteSettings,
                          admin_notification_email: e.target.value,
                        })
                      }
                      placeholder="Admin notification email"
                    />
                  </Form.Group>
                </Col>
              </Row>
            </SoftBlock>
          </Col>

          <Col xl={6}>
            <SoftBlock title="Cloudinary Image Storage">
              <Row className="g-3">
                <Col md={12}>
                  <Form.Check
                    type="switch"
                    label="Enable Cloudinary uploads"
                    checked={!!siteSettings.cloudinary_enabled}
                    onChange={(e) =>
                      setSiteSettings({
                        ...siteSettings,
                        cloudinary_enabled: e.target.checked,
                      })
                    }
                  />
                </Col>
                <Col md={12}>
                  <Form.Group>
                    <Form.Label>Cloud Name</Form.Label>
                    <Form.Control
                      value={siteSettings.cloudinary_cloud_name || ''}
                      onChange={(e) =>
                        setSiteSettings({
                          ...siteSettings,
                          cloudinary_cloud_name: e.target.value,
                        })
                      }
                      placeholder="Cloudinary cloud name"
                    />
                  </Form.Group>
                </Col>
                <Col md={12}>
                  <SensitiveField
                    label="API Key"
                    value={siteSettings.cloudinary_api_key || ''}
                    onChange={(e) =>
                      setSiteSettings({
                        ...siteSettings,
                        cloudinary_api_key: e.target.value,
                      })
                    }
                    placeholder="Cloudinary API key"
                  />
                </Col>
                <Col md={12}>
                  <SensitiveField
                    label="API Secret"
                    value={siteSettings.cloudinary_api_secret || ''}
                    onChange={(e) =>
                      setSiteSettings({
                        ...siteSettings,
                        cloudinary_api_secret: e.target.value,
                      })
                    }
                    placeholder={siteSettings.cloudinary_api_secret_masked || 'Cloudinary API secret'}
                  />
                </Col>
                <Col md={12}>
                  <Form.Group>
                    <Form.Label>Folder Prefix</Form.Label>
                    <Form.Control
                      value={siteSettings.cloudinary_folder || 'site-assets'}
                      onChange={(e) =>
                        setSiteSettings({
                          ...siteSettings,
                          cloudinary_folder: e.target.value,
                        })
                      }
                      placeholder="site-assets"
                    />
                  </Form.Group>
                </Col>
              </Row>
            </SoftBlock>
          </Col>

          <Col xs={12} className="d-flex justify-content-end">
            <Button onClick={saveSiteSettings} className="admin-action-btn">
              Save Settings
            </Button>
          </Col>
        </Row>
      </SectionCard>

      <SectionCard title="CMS Pages">
        <Row className="g-4">
          <Col xl={5}>
            <SoftBlock title={cmsPage.id ? 'Update CMS Page' : 'Create CMS Page'}>
              <Row className="g-3">
                <Col md={4}>
                  <Form.Group>
                    <Form.Label>Slug</Form.Label>
                    <Form.Control
                      value={cmsPage.slug}
                      onChange={(e) =>
                        setCmsPage({ ...cmsPage, slug: e.target.value })
                      }
                      placeholder="privacy"
                    />
                  </Form.Group>
                </Col>

                <Col md={8}>
                  <Form.Group>
                    <Form.Label>Title</Form.Label>
                    <Form.Control
                      value={cmsPage.title}
                      onChange={(e) =>
                        setCmsPage({ ...cmsPage, title: e.target.value })
                      }
                      placeholder="Page title"
                    />
                  </Form.Group>
                </Col>

                <Col md={12}>
                  <Form.Group>
                    <Form.Label>HTML Content</Form.Label>
                    <Form.Control
                      as="textarea"
                      rows={6}
                      value={cmsPage.content}
                      onChange={(e) =>
                        setCmsPage({ ...cmsPage, content: e.target.value })
                      }
                      placeholder="HTML content"
                    />
                  </Form.Group>
                </Col>

                <Col md={6}>
                  <Form.Group>
                    <Form.Label>SEO Title</Form.Label>
                    <Form.Control
                      value={cmsPage.seo_title}
                      onChange={(e) =>
                        setCmsPage({ ...cmsPage, seo_title: e.target.value })
                      }
                      placeholder="SEO title"
                    />
                  </Form.Group>
                </Col>

                <Col md={6}>
                  <Form.Group>
                    <Form.Label>SEO Description</Form.Label>
                    <Form.Control
                      value={cmsPage.seo_description}
                      onChange={(e) =>
                        setCmsPage({
                          ...cmsPage,
                          seo_description: e.target.value,
                        })
                      }
                      placeholder="SEO description"
                    />
                  </Form.Group>
                </Col>

                <Col md={4}>
                  <Form.Check
                    type="switch"
                    label="Show in Menu"
                    checked={!!cmsPage.show_in_menu}
                    onChange={(e) =>
                      setCmsPage({
                        ...cmsPage,
                        show_in_menu: e.target.checked,
                      })
                    }
                  />
                </Col>

                <Col md={4}>
                  <Form.Check
                    type="switch"
                    label="Show in Footer"
                    checked={!!cmsPage.show_in_footer}
                    onChange={(e) =>
                      setCmsPage({
                        ...cmsPage,
                        show_in_footer: e.target.checked,
                      })
                    }
                  />
                </Col>

                <Col md={4}>
                  <Form.Check
                    type="switch"
                    label="Published"
                    checked={!!cmsPage.is_published}
                    onChange={(e) =>
                      setCmsPage({
                        ...cmsPage,
                        is_published: e.target.checked,
                      })
                    }
                  />
                </Col>

                <Col xs={12} className="d-flex gap-2 justify-content-end">
                  {cmsPage.id ? (
                    <Button
                      variant="outline-secondary"
                      onClick={() => setCmsPage(emptyCmsPage)}
                      className="admin-action-btn"
                    >
                      Reset
                    </Button>
                  ) : null}
                  <Button onClick={saveCmsPage} className="admin-action-btn">
                    {cmsPage.id ? 'Update Page' : 'Create Page'}
                  </Button>
                </Col>
              </Row>
            </SoftBlock>
          </Col>

          <Col xl={7}>
            <SoftBlock title="All CMS Pages">
              <div className="table-responsive">
                <Table hover className="admin-table align-middle mb-0">
                  <thead>
                    <tr>
                      <th>Slug</th>
                      <th>Title</th>
                      <th>Menu</th>
                      <th>Footer</th>
                      <th>Published</th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {(cmsList.results || []).length ? (
                      (cmsList.results || []).map((p) => (
                        <tr key={p.id}>
                          <td>{p.slug}</td>
                          <td>{p.title}</td>
                          <td>{p.show_in_menu ? 'Yes' : 'No'}</td>
                          <td>{p.show_in_footer ? 'Yes' : 'No'}</td>
                          <td>{p.is_published ? 'Yes' : 'No'}</td>
                          <td>
                            <div className="d-flex gap-2 flex-wrap">
                              <Button
                                size="sm"
                                variant="outline-primary"
                                onClick={() => setCmsPage(p)}
                                className="admin-action-btn"
                              >
                                Edit
                              </Button>
                              <Button
                                size="sm"
                                variant="outline-danger"
                                onClick={() => deleteCmsPage(p.id)}
                                className="admin-action-btn"
                              >
                                Delete
                              </Button>
                            </div>
                          </td>
                        </tr>
                      ))
                    ) : (
                      <tr>
                        <td colSpan={6} className="text-center py-4">
                          No CMS pages found.
                        </td>
                      </tr>
                    )}
                  </tbody>
                </Table>
              </div>

              <PaginationControls
                page={cmsList.page}
                hasPrev={!!cmsList.previous}
                hasNext={!!cmsList.next}
                onPrev={() =>
                  cmsList.previous &&
                  load(history.page || 1, Math.max(1, cmsList.page - 1))
                }
                onNext={() =>
                  cmsList.next && load(history.page || 1, cmsList.page + 1)
                }
              />
            </SoftBlock>
          </Col>
        </Row>
      </SectionCard>

      <SectionCard title="Email Templates">
        <Row className="g-3">
          {templates.map((t, i) => (
            <Col xl={6} key={t.code}>
              <SoftBlock title={t.code}>
                <div className="d-flex justify-content-between align-items-center gap-2 mb-3">
                  <div className="small text-muted">Email template configuration</div>
                  <Form.Check
                    type="switch"
                    label="Active"
                    checked={!!t.active}
                    onChange={(e) => {
                      const copy = [...templates];
                      copy[i] = { ...t, active: e.target.checked };
                      setTemplates(copy);
                    }}
                  />
                </div>

                <Form.Group className="mb-3">
                  <Form.Label>Subject</Form.Label>
                  <Form.Control
                    value={t.subject}
                    onChange={(e) => {
                      const copy = [...templates];
                      copy[i] = { ...t, subject: e.target.value };
                      setTemplates(copy);
                    }}
                    placeholder="Subject"
                  />
                </Form.Group>

                <Form.Group className="mb-3">
                  <Form.Label>HTML Body</Form.Label>
                  <Form.Control
                    as="textarea"
                    rows={5}
                    value={t.body_html}
                    onChange={(e) => {
                      const copy = [...templates];
                      copy[i] = { ...t, body_html: e.target.value };
                      setTemplates(copy);
                    }}
                    placeholder="HTML body"
                  />
                </Form.Group>

                <div className="small text-muted mb-2">Preview</div>
                <div
                  className="border rounded p-3 bg-white"
                  dangerouslySetInnerHTML={{
                    __html: t.body_html || '<em>No template body</em>',
                  }}
                />
              </SoftBlock>
            </Col>
          ))}
        </Row>

        <div className="d-flex justify-content-end mt-3">
          <Button onClick={saveTemplates} className="admin-action-btn">
            Save Email Templates
          </Button>
        </div>
      </SectionCard>

      <SectionCard title="Payment Gateway Settings">
        <Row className="g-4">
          <Col xl={6}>
            <SoftBlock
              title="Stripe"
              className="h-100"
            >
              <Row className="g-3">
                <Col md={12}>
                  <Form.Check
                    type="switch"
                    label="Stripe enabled"
                    checked={!!gateway.stripe_enabled}
                    onChange={(e) =>
                      setGateway({
                        ...gateway,
                        stripe_enabled: e.target.checked,
                      })
                    }
                  />
                </Col>

                <Col md={12}>
                  <Form.Group>
                    <Form.Label>Public Key</Form.Label>
                    <Form.Control
                      value={gateway.stripe_public_key || ''}
                      onChange={(e) =>
                        setGateway({
                          ...gateway,
                          stripe_public_key: e.target.value,
                        })
                      }
                      placeholder="Stripe public key"
                    />
                  </Form.Group>
                </Col>

                <Col md={12}>
                  <SensitiveField
                    label="Secret Key"
                    value={gateway.stripe_secret_key || ''}
                    onChange={(e) =>
                      setGateway({
                        ...gateway,
                        stripe_secret_key: e.target.value,
                      })
                    }
                    placeholder="Stripe secret key"
                  />
                </Col>

                <Col md={12}>
                  <SensitiveField
                    label="Webhook Secret"
                    value={gateway.stripe_webhook_secret || ''}
                    onChange={(e) =>
                      setGateway({
                        ...gateway,
                        stripe_webhook_secret: e.target.value,
                      })
                    }
                    placeholder="Stripe webhook secret"
                  />
                </Col>
              </Row>
            </SoftBlock>
          </Col>

          <Col xl={6}>
            <SoftBlock
              title="Paystack"
              className="h-100"
            >
              <Row className="g-3">
                <Col md={12}>
                  <Form.Check
                    type="switch"
                    label="Paystack enabled"
                    checked={!!gateway.paystack_enabled}
                    onChange={(e) =>
                      setGateway({
                        ...gateway,
                        paystack_enabled: e.target.checked,
                      })
                    }
                  />
                </Col>

                <Col md={12}>
                  <Form.Group>
                    <Form.Label>Public Key</Form.Label>
                    <Form.Control
                      value={gateway.paystack_public_key || ''}
                      onChange={(e) =>
                        setGateway({
                          ...gateway,
                          paystack_public_key: e.target.value,
                        })
                      }
                      placeholder="Paystack public key"
                    />
                  </Form.Group>
                </Col>

                <Col md={12}>
                  <SensitiveField
                    label="Secret Key"
                    value={gateway.paystack_secret_key || ''}
                    onChange={(e) =>
                      setGateway({
                        ...gateway,
                        paystack_secret_key: e.target.value,
                      })
                    }
                    placeholder="Paystack secret key"
                  />
                </Col>

                <Col md={12}>
                  <Form.Group>
                    <Form.Label>Callback URL</Form.Label>
                    <Form.Control
                      value={gateway.paystack_callback_url || ''}
                      onChange={(e) =>
                        setGateway({
                          ...gateway,
                          paystack_callback_url: e.target.value,
                        })
                      }
                      placeholder="Paystack callback URL"
                    />
                  </Form.Group>
                </Col>
              </Row>
            </SoftBlock>
          </Col>

          <Col xs={12} className="d-flex justify-content-end">
            <Button onClick={saveGateways} className="admin-action-btn">
              Save Gateway Settings
            </Button>
          </Col>
        </Row>
      </SectionCard>

      <SectionCard title="Subscription Billing">
        {subscription ? (
          <Alert variant="info">
            Current: {subscription.plan?.name} ({subscription.provider} /{' '}
            {subscription.billing_cycle}) — Ends:{' '}
            {subscription.current_period_end
              ? new Date(subscription.current_period_end).toLocaleString()
              : 'N/A'}
          </Alert>
        ) : null}

        <div className="table-responsive">
          <Table hover className="admin-table align-middle mb-0">
            <thead>
              <tr>
                <th>Plan</th>
                <th>Monthly</th>
                <th>Annual</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {plans.length ? (
                plans.map((p) => (
                  <tr key={p.id}>
                    <td>{p.name}</td>
                    <td>${p.monthly_price_usd}</td>
                    <td>${p.annual_price_usd}</td>
                    <td>
                      <div className="d-flex gap-2 flex-wrap">
                        <Button
                          size="sm"
                          onClick={() =>
                            updateSubscription(p.id, 'stripe', 'monthly')
                          }
                          className="admin-action-btn"
                        >
                          Stripe Monthly
                        </Button>

                        <Button
                          size="sm"
                          onClick={() =>
                            updateSubscription(p.id, 'stripe', 'annual')
                          }
                          className="admin-action-btn"
                        >
                          Stripe Annual
                        </Button>

                        <Button
                          size="sm"
                          onClick={() =>
                            updateSubscription(p.id, 'paystack', 'monthly')
                          }
                          className="admin-action-btn"
                        >
                          Paystack
                        </Button>

                        <Button
                          size="sm"
                          onClick={() =>
                            startAdminCheckout(p.id, 'stripe', 'monthly')
                          }
                          className="admin-action-btn"
                        >
                          Stripe Checkout
                        </Button>

                        <Button
                          size="sm"
                          onClick={() =>
                            startAdminCheckout(p.id, 'paystack', 'monthly')
                          }
                          className="admin-action-btn"
                        >
                          Paystack Checkout
                        </Button>
                      </div>
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan={4} className="text-center py-4">
                    No plans available.
                  </td>
                </tr>
              )}
            </tbody>
          </Table>
        </div>
      </SectionCard>

      <SectionCard title="Plan Builder / Feature Matrix">
        <div className="table-responsive">
          <Table hover className="admin-table align-middle mb-0">
            <thead>
              <tr>
                <th>Code</th>
                <th>Name</th>
                <th>Monthly</th>
                <th>Annual</th>
                <th>Active</th>
                <th>Features & Limits</th>
              </tr>
            </thead>
            <tbody>
              {planCatalog.length ? (
                planCatalog.map((p, i) => (
                  <tr key={p.id || i}>
                    <td>
                      <Form.Control
                        value={p.code || ''}
                        onChange={(e) => {
                          const copy = [...planCatalog];
                          copy[i] = { ...p, code: e.target.value };
                          setPlanCatalog(copy);
                        }}
                      />
                    </td>

                    <td>
                      <Form.Control
                        value={p.name || ''}
                        onChange={(e) => {
                          const copy = [...planCatalog];
                          copy[i] = { ...p, name: e.target.value };
                          setPlanCatalog(copy);
                        }}
                      />
                    </td>

                    <td>
                      <Form.Control
                        value={p.monthly_price_usd || 0}
                        onChange={(e) => {
                          const copy = [...planCatalog];
                          copy[i] = {
                            ...p,
                            monthly_price_usd: e.target.value,
                          };
                          setPlanCatalog(copy);
                        }}
                      />
                    </td>

                    <td>
                      <Form.Control
                        value={p.annual_price_usd || 0}
                        onChange={(e) => {
                          const copy = [...planCatalog];
                          copy[i] = {
                            ...p,
                            annual_price_usd: e.target.value,
                          };
                          setPlanCatalog(copy);
                        }}
                      />
                    </td>

                    <td>
                      <Form.Check
                        checked={!!p.active}
                        onChange={(e) => {
                          const copy = [...planCatalog];
                          copy[i] = { ...p, active: e.target.checked };
                          setPlanCatalog(copy);
                        }}
                      />
                    </td>

                    <td style={{ minWidth: 360 }}>
                      <div className="d-flex flex-column gap-3">
                        <div>
                          <div className="small fw-semibold mb-2">Features</div>
                          <div className="d-flex flex-wrap gap-3">
                            {FEATURE_OPTIONS.map((f) => {
                              const meta = parsePlanMeta(p.metadata);
                              const enabled = !!meta?.features?.[f.code];
                              return (
                                <Form.Check
                                  key={`${p.id || i}-${f.code}`}
                                  type="switch"
                                  label={f.label}
                                  checked={enabled}
                                  onChange={(e) =>
                                    togglePlanFeature(i, f.code, e.target.checked)
                                  }
                                />
                              );
                            })}
                          </div>
                        </div>

                        <div>
                          <div className="small fw-semibold mb-2">Limits</div>
                          <Row className="g-2">
                            {LIMIT_OPTIONS.map((l) => {
                              const meta = parsePlanMeta(p.metadata);
                              const limitValue = meta?.limits?.[l.code] ?? '';
                              return (
                                <Col md={6} key={`${p.id || i}-${l.code}`}>
                                  <Form.Group>
                                    <Form.Label className="small">{l.label}</Form.Label>
                                    <Form.Control
                                      size="sm"
                                      type="number"
                                      value={limitValue}
                                      onChange={(e) =>
                                        setPlanLimit(i, l.code, e.target.value)
                                      }
                                      placeholder={l.label}
                                    />
                                  </Form.Group>
                                </Col>
                              );
                            })}
                          </Row>
                        </div>
                      </div>
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan={6} className="text-center py-4">
                    No plans configured.
                  </td>
                </tr>
              )}
            </tbody>
          </Table>
        </div>

        <div className="d-flex flex-wrap gap-2 justify-content-end mt-3">
          <Button
            className="admin-action-btn"
            onClick={() =>
              setPlanCatalog([
                ...(planCatalog || []),
                withPlanDefaults({
                  code: '',
                  name: '',
                  monthly_price_usd: 0,
                  annual_price_usd: 0,
                  active: true,
                  metadata: {},
                }),
              ])
            }
          >
            Add Plan
          </Button>

          <Button className="admin-action-btn" onClick={savePlanCatalog}>
            Save Plan Catalog
          </Button>

          <Button
            variant="outline-primary"
            className="admin-action-btn"
            onClick={reapplyEntitlements}
          >
            Reapply Entitlements
          </Button>
        </div>
      </SectionCard>

      <SectionCard title="Payment History">
        <div className="table-responsive">
          <Table hover className="admin-table align-middle mb-0">
            <thead>
              <tr>
                <th>Provider</th>
                <th>Event</th>
                <th>Processed</th>
                <th>Created</th>
              </tr>
            </thead>
            <tbody>
              {(history.results || []).length ? (
                (history.results || []).map((e) => (
                  <tr key={`${e.provider}-${e.event_id}`}>
                    <td>{e.provider}</td>
                    <td>{e.event_type}</td>
                    <td>{e.processed ? 'Yes' : 'No'}</td>
                    <td>{new Date(e.created_at).toLocaleString()}</td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan={4} className="text-center py-4">
                    No payment history found.
                  </td>
                </tr>
              )}
            </tbody>
          </Table>
        </div>

        <PaginationControls
          page={history.page}
          hasPrev={!!history.previous}
          hasNext={!!history.next}
          onPrev={() =>
            history.previous &&
            load(Math.max(1, history.page - 1), cmsList.page || 1)
          }
          onNext={() => history.next && load(history.page + 1, cmsList.page || 1)}
        />
      </SectionCard>

      <SectionCard
        title="WhatsApp Setup"
        right={
          <Badge bg={wa.webhook_enabled ? 'success' : 'secondary'} pill>
            {wa.webhook_enabled ? 'Enabled' : 'Disabled'}
          </Badge>
        }
      >
        <Row className="g-4">
          <Col xl={6}>
            <SoftBlock title="Connection Settings">
              <Row className="g-3">
                <Col md={4}>
                  <SensitiveField
                    label="Verify Token"
                    value={wa.verify_token || ''}
                    onChange={(e) =>
                      setWa({ ...wa, verify_token: e.target.value })
                    }
                    placeholder="Verify token"
                  />
                </Col>

                <Col md={4}>
                  <Form.Group>
                    <Form.Label>Phone Number ID</Form.Label>
                    <Form.Control
                      value={wa.phone_number_id || ''}
                      onChange={(e) =>
                        setWa({ ...wa, phone_number_id: e.target.value })
                      }
                      placeholder="Phone number ID"
                    />
                  </Form.Group>
                </Col>

                <Col md={4}>
                  <SensitiveField
                    label="API Token"
                    value={wa.api_token || ''}
                    onChange={(e) =>
                      setWa({ ...wa, api_token: e.target.value })
                    }
                    placeholder="API token"
                  />
                </Col>

                <Col md={6}>
                  <Form.Group>
                    <Form.Label>Support Email</Form.Label>
                    <Form.Control
                      value={wa.support_email || ''}
                      onChange={(e) =>
                        setWa({ ...wa, support_email: e.target.value })
                      }
                      placeholder="Support email"
                    />
                  </Form.Group>
                </Col>

                <Col md={6}>
                  <Form.Group>
                    <Form.Label>Support Phone</Form.Label>
                    <Form.Control
                      value={wa.support_phone || ''}
                      onChange={(e) =>
                        setWa({ ...wa, support_phone: e.target.value })
                      }
                      placeholder="Support phone"
                    />
                  </Form.Group>
                </Col>

                <Col md={12}>
                  <Form.Group>
                    <Form.Label>Support Address</Form.Label>
                    <Form.Control
                      value={wa.support_address || ''}
                      onChange={(e) =>
                        setWa({ ...wa, support_address: e.target.value })
                      }
                      placeholder="Support address"
                    />
                  </Form.Group>
                </Col>

                <Col md={12}>
                  <Form.Group>
                    <Form.Label>Allowed Media Types</Form.Label>
                    <Form.Control
                      value={wa.allowed_media_types || ''}
                      onChange={(e) =>
                        setWa({ ...wa, allowed_media_types: e.target.value })
                      }
                      placeholder="text,image,document"
                    />
                  </Form.Group>
                </Col>

                <Col md={6}>
                  <Form.Group>
                    <Form.Label>Max Messages Per Hour</Form.Label>
                    <Form.Control
                      type="number"
                      value={wa.max_messages_per_hour || 60}
                      onChange={(e) =>
                        setWa({
                          ...wa,
                          max_messages_per_hour: Number(e.target.value || 60),
                        })
                      }
                      placeholder="60"
                    />
                  </Form.Group>
                </Col>

                <Col md={6} className="d-flex align-items-end">
                  <Form.Check
                    type="switch"
                    label="Enable Webhook"
                    checked={!!wa.webhook_enabled}
                    onChange={(e) =>
                      setWa({ ...wa, webhook_enabled: e.target.checked })
                    }
                  />
                </Col>
              </Row>
            </SoftBlock>
          </Col>

          <Col xl={6}>
            <SoftBlock title="Menu Content">
              <Row className="g-3">
                <Col md={12}>
                  <Form.Group>
                    <Form.Label>Menu Title</Form.Label>
                    <Form.Control
                      value={wa.menu_title || ''}
                      onChange={(e) => setWa({ ...wa, menu_title: e.target.value })}
                      placeholder="Welcome"
                    />
                  </Form.Group>
                </Col>

                <Col md={12}>
                  <Form.Group>
                    <Form.Label>Menu Body</Form.Label>
                    <Form.Control
                      as="textarea"
                      rows={4}
                      value={wa.menu_body || ''}
                      onChange={(e) => setWa({ ...wa, menu_body: e.target.value })}
                      placeholder="Hello, how can we help?"
                    />
                  </Form.Group>
                </Col>

                <Col md={12}>
                  <Form.Group>
                    <Form.Label>Menu Button Text</Form.Label>
                    <Form.Control
                      value={wa.menu_button_text || ''}
                      onChange={(e) =>
                        setWa({ ...wa, menu_button_text: e.target.value })
                      }
                      placeholder="Menus"
                    />
                  </Form.Group>
                </Col>
              </Row>
            </SoftBlock>
          </Col>

          <Col xl={12}>
            <SoftBlock title="Menu Options">
              <div className="d-flex justify-content-between align-items-center flex-wrap gap-2 mb-3">
                <div className="small text-muted">
                  Configure button options shown in the WhatsApp menu.
                </div>
                <Button
                  size="sm"
                  variant="outline-secondary"
                  onClick={() => addWaOptionRow('menu_options', emptyMenuOption)}
                >
                  Add Option
                </Button>
              </div>

              {(wa.menu_options || []).length ? (
                (wa.menu_options || []).map((row, idx) => (
                  <Row className="g-3 mb-3" key={`admin-menu-${idx}`}>
                    <Col lg={3}>
                      <Form.Group>
                        <Form.Label>ID</Form.Label>
                        <Form.Control
                          value={row.id || ''}
                          placeholder="id"
                          onChange={(e) =>
                            updateWaOptionRow('menu_options', idx, {
                              id: e.target.value,
                            })
                          }
                        />
                      </Form.Group>
                    </Col>

                    <Col lg={4}>
                      <Form.Group>
                        <Form.Label>Title</Form.Label>
                        <Form.Control
                          value={row.title || ''}
                          placeholder="title"
                          onChange={(e) =>
                            updateWaOptionRow('menu_options', idx, {
                              title: e.target.value,
                            })
                          }
                        />
                      </Form.Group>
                    </Col>

                    <Col lg={4}>
                      <Form.Group>
                        <Form.Label>Description</Form.Label>
                        <Form.Control
                          value={row.description || ''}
                          placeholder="description"
                          onChange={(e) =>
                            updateWaOptionRow('menu_options', idx, {
                              description: e.target.value,
                            })
                          }
                        />
                      </Form.Group>
                    </Col>

                    <Col lg={1} className="d-flex align-items-end">
                      <Button
                        variant="outline-danger"
                        className="w-100"
                        onClick={() => removeWaOptionRow('menu_options', idx)}
                      >
                        ×
                      </Button>
                    </Col>
                  </Row>
                ))
              ) : (
                <div className="text-muted small">No menu options added yet.</div>
              )}
            </SoftBlock>
          </Col>

          <Col xl={6}>
            <SoftBlock title="Intro Quick Replies">
              <Row className="g-3 mb-3">
                <Col md={12}>
                  <Form.Group>
                    <Form.Label>Intro Prompt</Form.Label>
                    <Form.Control
                      value={wa.quick_reply_intro_text || ''}
                      onChange={(e) =>
                        setWa({
                          ...wa,
                          quick_reply_intro_text: e.target.value,
                        })
                      }
                      placeholder="How can I help you next?"
                    />
                  </Form.Group>
                </Col>
              </Row>

              <div className="d-flex justify-content-between align-items-center flex-wrap gap-2 mb-3">
                <div className="small text-muted">Intro-stage quick replies.</div>
                <Button
                  size="sm"
                  variant="outline-secondary"
                  onClick={() =>
                    addWaOptionRow('quick_reply_intro_options', emptyReplyOption)
                  }
                >
                  Add Reply
                </Button>
              </div>

              {(wa.quick_reply_intro_options || []).length ? (
                (wa.quick_reply_intro_options || []).map((row, idx) => (
                  <Row className="g-3 mb-3" key={`admin-intro-${idx}`}>
                    <Col md={5}>
                      <Form.Group>
                        <Form.Label>ID</Form.Label>
                        <Form.Control
                          value={row.id || ''}
                          placeholder="id"
                          onChange={(e) =>
                            updateWaOptionRow('quick_reply_intro_options', idx, {
                              id: e.target.value,
                            })
                          }
                        />
                      </Form.Group>
                    </Col>

                    <Col md={6}>
                      <Form.Group>
                        <Form.Label>Title</Form.Label>
                        <Form.Control
                          value={row.title || ''}
                          placeholder="title"
                          onChange={(e) =>
                            updateWaOptionRow('quick_reply_intro_options', idx, {
                              title: e.target.value,
                            })
                          }
                        />
                      </Form.Group>
                    </Col>

                    <Col md={1} className="d-flex align-items-end">
                      <Button
                        variant="outline-danger"
                        className="w-100"
                        onClick={() =>
                          removeWaOptionRow('quick_reply_intro_options', idx)
                        }
                      >
                        ×
                      </Button>
                    </Col>
                  </Row>
                ))
              ) : (
                <div className="text-muted small">No intro quick replies added yet.</div>
              )}
            </SoftBlock>
          </Col>

          <Col xl={6}>
            <SoftBlock title="Stuck-State Quick Replies">
              <Row className="g-3 mb-3">
                <Col md={12}>
                  <Form.Group>
                    <Form.Label>Stuck Prompt</Form.Label>
                    <Form.Control
                      value={wa.quick_reply_stuck_text || ''}
                      onChange={(e) =>
                        setWa({
                          ...wa,
                          quick_reply_stuck_text: e.target.value,
                        })
                      }
                      placeholder="Need help from a real person?"
                    />
                  </Form.Group>
                </Col>
              </Row>

              <div className="d-flex justify-content-between align-items-center flex-wrap gap-2 mb-3">
                <div className="small text-muted">Replies shown when user is stuck.</div>
                <Button
                  size="sm"
                  variant="outline-secondary"
                  onClick={() =>
                    addWaOptionRow('quick_reply_stuck_options', emptyReplyOption)
                  }
                >
                  Add Reply
                </Button>
              </div>

              {(wa.quick_reply_stuck_options || []).length ? (
                (wa.quick_reply_stuck_options || []).map((row, idx) => (
                  <Row className="g-3 mb-3" key={`admin-stuck-${idx}`}>
                    <Col md={5}>
                      <Form.Group>
                        <Form.Label>ID</Form.Label>
                        <Form.Control
                          value={row.id || ''}
                          placeholder="id"
                          onChange={(e) =>
                            updateWaOptionRow('quick_reply_stuck_options', idx, {
                              id: e.target.value,
                            })
                          }
                        />
                      </Form.Group>
                    </Col>

                    <Col md={6}>
                      <Form.Group>
                        <Form.Label>Title</Form.Label>
                        <Form.Control
                          value={row.title || ''}
                          placeholder="title"
                          onChange={(e) =>
                            updateWaOptionRow('quick_reply_stuck_options', idx, {
                              title: e.target.value,
                            })
                          }
                        />
                      </Form.Group>
                    </Col>

                    <Col md={1} className="d-flex align-items-end">
                      <Button
                        variant="outline-danger"
                        className="w-100"
                        onClick={() =>
                          removeWaOptionRow('quick_reply_stuck_options', idx)
                        }
                      >
                        ×
                      </Button>
                    </Col>
                  </Row>
                ))
              ) : (
                <div className="text-muted small">
                  No stuck-state quick replies added yet.
                </div>
              )}
            </SoftBlock>
          </Col>

          <Col xl={12}>
            <SoftBlock title="Action Response Map">
              <div className="d-flex justify-content-between align-items-center flex-wrap gap-2 mb-3">
                <div className="small text-muted">
                  Map action ids to automatic reply messages.
                </div>
                <Button
                  size="sm"
                  variant="outline-secondary"
                  onClick={() =>
                    setWaActionRows([...(waActionRows || []), emptyActionMap])
                  }
                >
                  Add Mapping
                </Button>
              </div>

              {waActionRows.length ? (
                waActionRows.map((row, idx) => (
                  <Row className="g-3 mb-3" key={`admin-map-${idx}`}>
                    <Col lg={3}>
                      <Form.Group>
                        <Form.Label>Action ID</Form.Label>
                        <Form.Control
                          value={row.id || ''}
                          placeholder="action id"
                          onChange={(e) => {
                            const next = [...waActionRows];
                            next[idx] = { ...next[idx], id: e.target.value };
                            setWaActionRows(next);
                          }}
                        />
                      </Form.Group>
                    </Col>

                    <Col lg={8}>
                      <Form.Group>
                        <Form.Label>Auto Response Text</Form.Label>
                        <Form.Control
                          value={row.text || ''}
                          placeholder="Auto-response text"
                          onChange={(e) => {
                            const next = [...waActionRows];
                            next[idx] = { ...next[idx], text: e.target.value };
                            setWaActionRows(next);
                          }}
                        />
                      </Form.Group>
                    </Col>

                    <Col lg={1} className="d-flex align-items-end">
                      <Button
                        variant="outline-danger"
                        className="w-100"
                        onClick={() =>
                          setWaActionRows(
                            (waActionRows || []).filter((_, i) => i !== idx)
                          )
                        }
                      >
                        ×
                      </Button>
                    </Col>
                  </Row>
                ))
              ) : (
                <div className="text-muted small">No action mappings added yet.</div>
              )}
            </SoftBlock>
          </Col>

          <Col xs={12} className="d-flex justify-content-end">
            <Button onClick={saveWhatsApp} className="admin-action-btn">
              Save WhatsApp
            </Button>
          </Col>
        </Row>
      </SectionCard>

      <SectionCard
        title="Live Support Agents"
        right={<Badge bg="primary" pill>{agents.length}</Badge>}
      >
        <Row className="g-4">
          {agents.length === 0 ? (
            <Col xs={12}>
              <SoftBlock title="No Agents Added">
                <div className="text-muted">
                  Add support agents here. New agents receive their login setup email and can handle handovers from both web chat and WhatsApp.
                </div>
              </SoftBlock>
            </Col>
          ) : (
            agents.map((agent, idx) => (
              <Col xl={6} key={agent.id || `agent-${idx}`}>
                <SoftBlock title={`Agent ${idx + 1}`}>
                  <Row className="g-3">
                    <Col md={6}>
                      <Form.Group>
                        <Form.Label>Name</Form.Label>
                        <Form.Control
                          value={agent.name || ''}
                          onChange={(e) => {
                            const next = [...agents];
                            next[idx] = { ...next[idx], name: e.target.value };
                            setAgents(next);
                          }}
                          placeholder="Agent name"
                        />
                      </Form.Group>
                    </Col>
                    <Col md={6}>
                      <Form.Group>
                        <Form.Label>Email</Form.Label>
                        <Form.Control
                          value={agent.email || ''}
                          onChange={(e) => {
                            const next = [...agents];
                            next[idx] = { ...next[idx], email: e.target.value };
                            setAgents(next);
                          }}
                          placeholder="Agent email"
                        />
                      </Form.Group>
                    </Col>
                    <Col md={6}>
                      <SensitiveField
                        label="Password (optional)"
                        value={agent.password || ''}
                        onChange={(e) => {
                          const next = [...agents];
                          next[idx] = { ...next[idx], password: e.target.value };
                          setAgents(next);
                        }}
                        placeholder="Leave blank to auto-generate"
                      />
                    </Col>
                    <Col md={6}>
                      <Form.Group>
                        <Form.Label>Max Active Chats</Form.Label>
                        <Form.Control
                          type="number"
                          min="1"
                          value={agent.max_active_chats || 10}
                          onChange={(e) => {
                            const next = [...agents];
                            next[idx] = {
                              ...next[idx],
                              max_active_chats: Number(e.target.value || 10),
                            };
                            setAgents(next);
                          }}
                        />
                      </Form.Group>
                    </Col>
                    <Col md={6}>
                      <Form.Check
                        type="switch"
                        label="Online"
                        checked={!!agent.is_online}
                        onChange={(e) => {
                          const next = [...agents];
                          next[idx] = { ...next[idx], is_online: e.target.checked };
                          setAgents(next);
                        }}
                      />
                    </Col>
                    <Col md={6}>
                      <Form.Check
                        type="switch"
                        label="Active"
                        checked={!!agent.is_active}
                        onChange={(e) => {
                          const next = [...agents];
                          next[idx] = { ...next[idx], is_active: e.target.checked };
                          setAgents(next);
                        }}
                      />
                    </Col>
                    <Col xs={12}>
                      <Button
                        variant="outline-danger"
                        className="w-100"
                        onClick={() => removeSupportAgent(idx)}
                      >
                        Remove Agent
                      </Button>
                    </Col>
                  </Row>
                </SoftBlock>
              </Col>
            ))
          )}
          <Col xs={12} className="d-flex justify-content-between flex-wrap gap-2">
            <Button variant="outline-secondary" onClick={addSupportAgent}>
              Add Agent
            </Button>
            <Button className="admin-action-btn" onClick={saveSupportAgents}>
              Save Support Agents
            </Button>
          </Col>
        </Row>
      </SectionCard>

      <SectionCard title="Feature Entitlements / Gating">
        <div className="table-responsive">
          <Table hover className="admin-table align-middle mb-0">
            <thead>
              <tr>
                <th>Feature</th>
                <th>Enabled</th>
                <th>Limit</th>
              </tr>
            </thead>
            <tbody>
              {entitlements.length ? (
                entitlements.map((ent, idx) => (
                  <tr key={ent.id || ent.feature_code}>
                    <td>{ent.feature_code}</td>
                    <td>
                      <Form.Check
                        type="switch"
                        checked={!!ent.enabled}
                        onChange={(e) => {
                          const copy = [...entitlements];
                          copy[idx] = { ...ent, enabled: e.target.checked };
                          setEntitlements(copy);
                        }}
                      />
                    </td>
                    <td>
                      <Form.Control
                        value={ent.limit_value ?? ''}
                        onChange={(e) => {
                          const copy = [...entitlements];
                          copy[idx] = {
                            ...ent,
                            limit_value:
                              e.target.value === '' ? null : Number(e.target.value),
                          };
                          setEntitlements(copy);
                        }}
                      />
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan={3} className="text-center py-4">
                    No entitlements found.
                  </td>
                </tr>
              )}
            </tbody>
          </Table>
        </div>

        <div className="d-flex justify-content-end mt-3">
          <Button onClick={saveEntitlements} className="admin-action-btn">
            Save Entitlements
          </Button>
        </div>
      </SectionCard>
    </AdminLayout>
  );
}
